import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ErroApi, api } from '../api';
import {
  ACONDICIONAMENTO, TIPOS_AMOSTRA, data, dataHora, dispositivoResumido,
  faixaProfundidade, numero, plural, rotulo, rotuloDoAcondicionamento,
} from '../formato';
import { useSessao } from '../sessao';
import {
  Area, Aviso, BarraDeAvanco, Campo, Carregando, Dialogo, ErroDaApi, Escolha,
  Vazio, useEstreito, type Opcao,
} from '../ui';
import { botoesDoEnsaio, motivoSemAcao } from '../acaoDoEnsaio';
import { TrilhaDaFicha } from '../Trilha';
import { NomeDaAmostra, daApi, nomeDaAmostra } from '../NomeDaAmostra';
import { ExcluirDeVez } from './ExclusaoDefinitiva';

/**
 * A tela que o QR abre.
 *
 * A pergunta que o técnico faz de pé na prateleira, com a amostra na mão,
 * é uma só: **isto aqui ainda serve para alguma coisa?** Responder exige
 * saber se os ensaios acabaram, se o laudo saiu e quanto tempo de guarda
 * ainda resta — três informações que hoje moram em três planilhas.
 *
 * **A situação é calculada, nunca gravada** (visão `vw_amostra_situacao`).
 * Uma coluna `concluida` teria de ser atualizada a cada aprovação de
 * ensaio e de relatório, e no dia em que um caminho esquecesse, a
 * prateleira mentiria: ou descarta amostra com ensaio pendente, ou guarda
 * espaço por meses depois de liberada.
 *
 * **Amostra sem ensaio nenhum não conta como concluída.** É o caso mais
 * perigoso — chegou, ninguém programou nada, e "zero ensaios pendentes"
 * pareceria serviço terminado.
 */

interface Ensaio {
  id: string;
  status: string;
  metodo: string;
  metodo_codigo: string;
  // ── a bancada (onda 373), da `vw_ensaio` ──
  /** Fora de aprovado, cancelado e substituído. */
  aberto: boolean;
  /** Onde se lança este método. Nulo = ainda não há ficha (0076). */
  telas_lancamento: string[] | null;
  /** Por que este ensaio ainda não pode começar — ou nulo, se pode (0078). */
  impedimento_amostra: string | null;
  /** Verdadeiro quando o impedimento não passa com o tempo. */
  impedimento_definitivo: boolean | null;
  relatorio: string | null;
  relatorio_revisao: number | null;
  relatorio_status: string | null;
  data_aprovacao: string | null;
  // ── entrega ao cliente (0175) ──
  relatorio_id: string | null;
  data_emissao: string | null;
  disponibilizado_em: string | null;
}

interface Situacao {
  ensaios: string | number;
  ensaios_aprovados: string | number;
  ensaios_pendentes: string | number;
  relatorios_aprovados: string | number;
  relatorio_aprovado_em: string | null;
  prazo_guarda_dias: number | null;
  concluida: boolean;
  descarte_a_partir_de: string | null;
  dias_para_descarte: number | null;
  // ── avanço (0064) ──
  ensaios_devolvidos: string | number;
  ensaios_na_conta: string | number;
  avanco_pct: number | null;
  // ── descarte (0175): a contagem começa na entrega ao cliente ──
  relatorio_disponibilizado_em: string | null;
  ensaios_sem_laudo_entregue: string | number;
  ensaios_sem_laudo_emitido: string | number;
  entregue: boolean;
  encerrada: boolean;
  retida: boolean;
  retencao_id: string | null;
  retencao_ate: string | null;
  retencao_motivo: string | null;
  retencao_pedido_por: string | null;
  descarte_autorizado: boolean;
  autorizacao_id: string | null;
  autorizacao_valida_ate: string | null;
  autorizacao_motivo: string | null;
  elegivel_descarte: boolean;
  impedimento_descarte: string | null;
  impedimento_texto: string | null;
}

interface Amostra {
  id: string; codigo: string; status: string;
  identificacao_recebida: string | null;
  ponto_investigacao: string | null;
  profundidade_inicial: string | null;
  profundidade_final: string | null;
  camada: string | null;
  tipo_amostra: string | null;
  tipo_acondicionamento: string | null;
  acondicionamento_descricao: string | null;
  condicoes_embalagem: string | null;
  quantidade_aproximada: string | null;
  data_coleta: string | null;
  localizacao_fisica: string | null;
  cliente: string | null;
  obra_codigo: string | null; obra_nome: string | null;
  os_id: string | null; os_numero: string | null;
  lote_recebimento_id: string | null; lote_codigo: string | null;
  datahora_recebimento: string | null;
  movimentacoes: string | number;
  subdivisoes: string | number;
  situacao: Situacao | null;
  ensaios: Ensaio[];
  // ── custódia (0062) ──
  estado_custodia: string;
  local_id: string | null;
  local_nome: string | null;
  local_tipo_nome: string | null;
  local_registrado_em: string | null;
  local_registrado_por_nome: string | null;
  motivo_encerramento: string | null;
  encerrada_em: string | null;
  encerrada_por_nome: string | null;
  onde_esta: string;
  precisa_guardar: boolean;
}

interface LocalOpcao {
  id: string; nome: string; tipo_nome: string;
  armazenadas: number; situacao: string;
}

export function AmostraTela() {
  const { codigo = '' } = useParams();
  const [a, setA] = useState<Amostra | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);
  const [encerrando, setEncerrando] = useState(false);
  const [corrigindo, setCorrigindo] = useState(false);
  const { pode } = useSessao();
  /* Onda 373: no celular os dados da amostra começam recolhidos (opção A,
     aprovada pelo Humberto) — quem lê o QR quer o ensaio, não a ficha. */
  const estreito = useEstreito();
  const [verDados, setVerDados] = useState<boolean | null>(null);
  const dadosAbertos = verDados ?? !estreito;

  const carregar = useCallback(async () => {
    try {
      setA(await api<Amostra>(`/amostras/codigo/${encodeURIComponent(codigo)}`));
      setErro(null);
    } catch (e) { setErro(e); }
  }, [codigo]);

  useEffect(() => { void carregar(); }, [carregar]);

  if (erro && !a) return <ErroDaApi erro={erro} />;
  if (!a) return <Carregando texto="Carregando amostra" />;

  const acond = a.acondicionamento_descricao || rotulo(a.tipo_acondicionamento);

  return (
    <div>
      <div className="linha-topo">
        <div className="cresce">
          {/*
            * Onda 303 (RN-248): o título é o nome que o cliente deu
            * («SP-01/2»), com o código único logo abaixo, menor. O endereço
            * da tela e o QR continuam pelo código.
            */}
          <h1><NomeDaAmostra amostra={daApi(a)} forte={false} /></h1>
          {/*
            * Dois selos, e não um. Até a onda 23, ACEITA, EM ENSAIO e
            * DESCARTADA dividiam um selo só — mas são dois eixos: COMO
            * ela foi aceita na porta, e ONDE ela está no ciclo. Uma
            * amostra pode estar aceita com ressalva E ensaiando ao mesmo
            * tempo, e com um selo só uma das duas verdades se perdia.
            */}
          <span className="selo">{rotulo(a.status)}</span>
          <span className="selo">
            {ESTAGIOS[a.estado_custodia]?.texto ?? rotulo(a.estado_custodia)}
          </span>
        </div>
        {!TERMINAIS.has(a.estado_custodia) && (
          <>
            <button type="button" className="botao" onClick={() => setGuardando(true)}>
              Guardar
            </button>
            <button
              type="button"
              className="botao secundario"
              onClick={() => setEncerrando(true)}
            >
              Encerrar
            </button>
          </>
        )}
        {/*
          * Corrigir a ficha (UX-042). A API aceitava `PATCH /amostras/:id`
          * desde a 0062 e nenhuma tela chamava: o tipo errado digitado na
          * porta só se consertava pelo banco. Fica aberto mesmo depois de
          * encerrada — a ficha de uma amostra descartada ainda pode ter a
          * camada errada, e o laudo ainda a cita. O motivo é obrigatório
          * e vai para a trilha.
          */}
        {pode('amostra.movimentar') && (
          <button type="button" className="botao secundario"
            onClick={() => setCorrigindo(true)}>
            Corrigir a ficha
          </button>
        )}
        <ExcluirDeVez tipo="amostra" id={a.id} />
      </div>

      {corrigindo && (
        <FormCorrigir
          amostra={a}
          aoFechar={() => setCorrigindo(false)}
          aoPronto={() => { setCorrigindo(false); void carregar(); }}
        />
      )}

      {guardando && (
        <FormGuardar
          amostra={a}
          aoFechar={() => setGuardando(false)}
          aoPronto={() => { setGuardando(false); void carregar(); }}
        />
      )}

      {encerrando && (
        <FormEncerrar
          amostra={a}
          aoFechar={() => setEncerrando(false)}
          aoPronto={() => { setEncerrando(false); void carregar(); }}
        />
      )}

      {a.precisa_guardar && (
        <Aviso tipo="atencao" titulo="Ninguém registrou onde esta amostra está.">
          O sistema sabe sozinho quando um ensaio começa; não sabe que
          alguém a devolveu à prateleira. É a única coisa que precisa de um
          toque — e sem ela o inventário do laboratório não fecha.
        </Aviso>
      )}

      {/*
        * Onda 373 (o QR leva à bancada): os ensaios sobem para logo abaixo
        * do cabeçalho, cada um com o botão de executar. Até a 372 eles
        * vinham depois de uma tabela de quatorze linhas — no celular, duas
        * telas de rolagem até o primeiro ensaio.
        */}
      <Programacao amostra={a} />

      <Guarda situacao={a.situacao} encerrada={TERMINAIS.has(a.estado_custodia)}
        motivo={a.motivo_encerramento} amostra={a} aoMudar={() => void carregar()} />

      {/*
        * Os dados da amostra: abertos no PC, recolhidos no celular atrás de
        * «Detalhes da amostra» (onda 373). O botão só existe no celular — no
        * PC a tabela cabe ao lado do resto e esconder não economiza nada.
        */}
      {estreito && (
        <button type="button" className="botao secundario largo detalhes-da-amostra"
          aria-expanded={dadosAbertos} aria-controls="dados-da-amostra"
          onClick={() => setVerDados(!dadosAbertos)}>
          {dadosAbertos ? 'Esconder os detalhes da amostra' : 'Detalhes da amostra'}
        </button>
      )}
      <div className="cartao dados-da-amostra" id="dados-da-amostra" hidden={!dadosAbertos}>
        <table className="dados">
          <tbody>
            {/* 303: o código único tem a sua linha — o título agora é o nome
                do cliente, e o código é o que a etiqueta e o QR carregam. */}
            <tr><th style={{ width: '40%' }}>Código único</th>
                <td className="mono">{a.codigo}</td></tr>
            <tr><th>Cliente</th>
                <td>{a.cliente ?? <span className="nada">—</span>}</td></tr>
            <tr><th>OS</th>
                <td>{a.os_numero
                     ?? <span className="nada">sem OS — nenhum ensaio pode começar</span>}</td></tr>
            <tr><th>Obra</th>
                <td>{a.obra_nome ?? <span className="nada">—</span>}</td></tr>
            <tr><th>Identificação recebida</th>
                <td>{a.identificacao_recebida ?? <span className="nada">—</span>}</td></tr>
            <tr><th>Furo / ponto</th>
                <td>
                  {a.ponto_investigacao ?? <span className="nada">—</span>}
                  {faixaProfundidade(a.profundidade_inicial, a.profundidade_final)
                    && ` · ${faixaProfundidade(a.profundidade_inicial, a.profundidade_final)} m`}
                </td></tr>
            <tr><th>Tipo</th>
                <td>{[rotulo(a.tipo_amostra), acond].filter(Boolean).join(' · ')
                     || <span className="nada">—</span>}</td></tr>
            <tr><th>Camada</th>
                <td>{a.camada ?? <span className="nada">—</span>}</td></tr>
            <tr><th>Chegada</th>
                <td>
                  {a.datahora_recebimento
                    ? dataHora(a.datahora_recebimento)
                    : <span className="nada">—</span>}
                  {a.lote_codigo && a.lote_recebimento_id && (
                    <> · <Link to={`/recebimento/${a.lote_recebimento_id}?amostra=${a.id}`}>
                      {a.lote_codigo}
                    </Link></>
                  )}
                </td></tr>
            <tr><th>Como chegou</th>
                <td>{a.condicoes_embalagem ?? <span className="nada">—</span>}</td></tr>
            <tr><th>Coleta</th>
                <td>{a.data_coleta ? data(a.data_coleta) : <span className="nada">não informada</span>}</td></tr>
            <tr><th>Onde está</th>
                <td>
                  {a.precisa_guardar
                    ? <span className="nada">{a.onde_esta}</span>
                    : a.onde_esta}
                  {a.local_registrado_em && a.local_registrado_por_nome && (
                    <div className="sub">
                      registrado por {a.local_registrado_por_nome} em{' '}
                      {dataHora(a.local_registrado_em)}
                    </div>
                  )}
                  {a.encerrada_em && a.encerrada_por_nome && (
                    <div className="sub">
                      por {a.encerrada_por_nome} em {dataHora(a.encerrada_em)}
                    </div>
                  )}
                </td></tr>
            <tr><th>Quantidade</th>
                <td>{a.quantidade_aproximada
                     ? numero(a.quantidade_aproximada, 2, true)
                     : <span className="nada">—</span>}</td></tr>
            <tr><th>Movimentações</th>
                <td>{plural(Number(a.movimentacoes), 'registro', 'registros')}
                    {Number(a.subdivisoes) > 0
                      && ` · ${plural(Number(a.subdivisoes), 'subdivisão', 'subdivisões')}`}</td></tr>
          </tbody>
        </table>
      </div>

      <Custodia
        amostraId={a.id}
        estado={a.estado_custodia}
        motivo={a.motivo_encerramento}
        chave={String(a.movimentacoes)}
      />
      <TrilhaDaFicha
        key={`trilha-${a.tipo_amostra}-${a.tipo_acondicionamento}-${a.camada}-${a.ponto_investigacao}-${a.identificacao_recebida}-${a.estado_custodia}`}
        rota={`/amostras/${a.id}/trilha`}
        titulo="Trilha de alterações da ficha"
        explica="Quem mudou o quê nesta amostra — tipo, método de coleta, camada, custódia — com o valor anterior, o novo e o motivo."
      />
    </div>
  );
}

// ── Corrigir a ficha ─────────────────────────────────────────────────

/**
 * O que a porta gravou errado, consertado com motivo.
 *
 * Só os campos que o laudo cita e que não vêm de nenhum outro registro:
 * identificação recebida, furo/ponto, tipo, método de coleta e camada.
 * Profundidade e data de coleta ficam de fora de propósito por enquanto —
 * mudam o que o ensaio já usou como identificação do corpo de prova.
 *
 * Só manda o que MUDOU. A API usa `COALESCE`, então mandar tudo seria
 * inofensivo — mas a trilha registraria «alterou» campo por campo em
 * cada gravação, e a trilha é para quem lê depois.
 */
function FormCorrigir({ amostra: a, aoFechar, aoPronto }: {
  amostra: Amostra; aoFechar(): void; aoPronto(): void;
}) {
  const [identificacao, setIdentificacao] = useState(a.identificacao_recebida ?? '');
  const [ponto, setPonto] = useState(a.ponto_investigacao ?? '');
  const [tipo, setTipo] = useState<string | null>(a.tipo_amostra);
  const [acond, setAcond] = useState<string | null>(a.tipo_acondicionamento);
  const [descricao, setDescricao] = useState(a.acondicionamento_descricao ?? '');
  const [camada, setCamada] = useState(a.camada ?? '');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const opcoesTipo: Opcao[] = TIPOS_AMOSTRA.map((v) => ({ valor: v, texto: rotulo(v) }));
  const opcoesAcond: Opcao[] = (tipo ? ACONDICIONAMENTO[tipo] ?? [] : [])
    .map((v) => ({ valor: v, texto: rotulo(v) }));
  const doCampo = (campo: string) =>
    (erro instanceof ErroApi ? erro.campo(campo) : undefined);

  const mudou = {
    identificacaoRecebida: identificacao.trim() !== (a.identificacao_recebida ?? '')
      ? identificacao.trim() : undefined,
    pontoInvestigacao: ponto.trim() !== (a.ponto_investigacao ?? '')
      ? ponto.trim() : undefined,
    tipoAmostra: tipo && tipo !== a.tipo_amostra ? tipo : undefined,
    tipoAcondicionamento: acond && acond !== a.tipo_acondicionamento ? acond : undefined,
    acondicionamentoDescricao: acond === 'outro'
      && descricao.trim() !== (a.acondicionamento_descricao ?? '')
      ? descricao.trim() : undefined,
    camada: camada.trim() !== (a.camada ?? '') ? camada.trim() : undefined,
  };
  const algo = Object.values(mudou).some((v) => v !== undefined);
  // Tipo novo sem método de coleta compatível: o banco recusaria.
  const acondCombina = !tipo || !acond || (ACONDICIONAMENTO[tipo] ?? []).includes(acond);
  const pronto = algo && motivo.trim().length >= 5 && acondCombina
    && (acond !== 'outro' || descricao.trim().length > 0);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    try {
      await api(`/amostras/${a.id}`, {
        metodo: 'PATCH',
        corpo: { ...mudou, motivo: motivo.trim() },
      });
      aoPronto();
    } catch (x) { setErro(x); } finally { setEnviando(false); }
  };

  return (
    <Dialogo aberto titulo={`Corrigir a ficha de ${nomeDaAmostra(daApi(a))}`} aoFechar={aoFechar}>
      <form onSubmit={enviar}>
        <ErroDaApi erro={erro} />
        <Campo
          rotulo="Identificação recebida"
          value={identificacao}
          onChange={(e) => setIdentificacao(e.currentTarget.value)}
          erro={doCampo('identificacaoRecebida')}
          dica="O que está escrito no saco, no tubo ou na etiqueta do cliente."
        />
        <Campo
          rotulo="Furo ou ponto"
          value={ponto}
          onChange={(e) => setPonto(e.currentTarget.value)}
          erro={doCampo('pontoInvestigacao')}
        />
        <Escolha
          rotulo="Tipo"
          opcoes={opcoesTipo}
          valor={tipo}
          limpavel={false}
          aoEscolher={(v) => {
            setTipo(v);
            if (v && acond && !(ACONDICIONAMENTO[v] ?? []).includes(acond)) setAcond(null);
          }}
          erro={doCampo('tipoAmostra')}
        />
        {tipo && (
          <Escolha
            rotulo={rotuloDoAcondicionamento(tipo)}
            opcoes={opcoesAcond}
            valor={acond}
            limpavel={false}
            aoEscolher={setAcond}
            erro={doCampo('tipoAcondicionamento')
              ?? (!acond ? 'Escolha o método de coleta compatível com o tipo.' : undefined)}
          />
        )}
        {acond === 'outro' && (
          <Campo
            rotulo="Diga o que é"
            value={descricao}
            onChange={(e) => setDescricao(e.currentTarget.value)}
            erro={doCampo('acondicionamentoDescricao')}
            dica="Tubo de PVC, cilindro de CBR, lascas, balde."
          />
        )}
        <Campo
          rotulo="Camada"
          value={camada}
          onChange={(e) => setCamada(e.currentTarget.value)}
          erro={doCampo('camada')}
        />
        <Area
          rotulo="Motivo da correção"
          rows={2}
          value={motivo}
          onChange={(e) => setMotivo(e.currentTarget.value)}
          erro={doCampo('motivo')}
          dica="Obrigatório. Fica na trilha ao lado do valor antigo e do novo."
        />
        {!algo && (
          <p className="sub" role="status">Nada mudou ainda.</p>
        )}
        <div className="rodape-dialogo">
          <button type="button" className="botao secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="submit" className="botao" disabled={enviando || !pronto}>
            {enviando ? 'Gravando…' : 'Gravar correção'}
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

// ── Cadeia de custódia ───────────────────────────────────────────────

/**
 * Estados dos quais a amostra não volta. Quem recusa é o banco
 * (`fn_custodia_transicao_permitida`); esta lista só evita oferecer na
 * tela um botão que o servidor vai negar — a regra continua sendo do
 * banco, e a tela apenas não mente sobre ela.
 */
const TERMINAIS = new Set(['encerrada']);

/**
 * Os quatro estágios, como o técnico os chama.
 *
 * `a_guardar` não é um quinto estágio inventado: é a pendência ficando
 * visível. O sistema sabe sozinho quando um ensaio começou; NÃO sabe que
 * alguém devolveu a amostra à prateleira 3 quando acabou. Derivar isso
 * gravaria com confiança um lugar que ninguém conferiu — pior que campo
 * vazio, porque mente em vez de omitir.
 */
const ESTAGIOS: Record<string, { texto: string; diz: string }> = {
  a_guardar: {
    texto: 'Falta guardar',
    diz: 'Ninguém registrou onde ela está. É a única coisa que o sistema '
      + 'não consegue descobrir sozinho.',
  },
  armazenada: { texto: 'Armazenada', diz: 'No lugar dela, pronta para uso.' },
  em_preparacao: {
    texto: 'Em preparação',
    diz: 'Secagem, destorroamento, quarteamento — o método de preparação '
      + 'está em execução.',
  },
  ensaiando: { texto: 'Ensaiando', diz: 'Em ensaio agora, na bancada.' },
  encerrada: { texto: 'Encerrada', diz: 'Saiu da custódia da Testesolo.' },
};

/**
 * As três saídas.
 *
 * São fatos diferentes e a cadeia de custódia precisa dizer qual foi.
 * Chamar as três de "descarte" faria o registro afirmar que um material
 * que ainda existe foi destruído — e é exatamente esse registro que uma
 * auditoria vai procurar.
 */
const SAIDAS: { valor: string; texto: string; diz: string; desabilitada?: boolean; porque?: string }[] = [
  {
    valor: 'consumida', texto: 'Consumida no ensaio',
    diz: 'O material acabou durante o ensaio. Destino normal de uma '
      + 'deformada — não houve ato de descarte.',
  },
  {
    valor: 'devolvida', texto: 'Devolvida ao cliente',
    diz: 'Saiu do prédio de volta para quem entregou, e NÃO foi destruída. '
      + 'É o caso do cliente que cancela o ensaio e pede o restante.',
  },
  {
    valor: 'descartada', texto: 'Descartada',
    diz: 'Descarte deliberado, depois do laudo entregue ao cliente e do '
      + 'prazo de guarda — ou com autorização do gestor/RT. Antes disso '
      + 'o banco recusa (RN-183); retida a pedido do cliente, nunca (RN-184).',
  },
];

interface Evento {
  tipo_evento: string;
  localizacao_fisica: string | null;
  datahora: string;
  dispositivo: string | null;
  observacao: string | null;
  usuario_email: string | null;
  usuario_nome: string | null;
}

/**
 * A cadeia de custódia, em ordem de acontecimento.
 *
 * Fica na tela e não só no banco porque a pergunta que ela responde é de
 * auditoria e de prateleira ao mesmo tempo: *quem tirou isto daqui, e
 * quando?* O contador de movimentações que existia antes dizia que houve
 * cinco — e nenhuma delas.
 *
 * Recarrega quando `chave` muda, isto é, quando uma movimentação nova
 * entra: o número de movimentações da amostra é a própria chave.
 */
function Custodia({
  amostraId, estado, motivo, chave,
}: {
  amostraId: string; estado: string; motivo: string | null; chave: string;
}) {
  const [lista, setLista] = useState<Evento[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  useEffect(() => {
    const abortar = new AbortController();
    api<Evento[]>(`/amostras/${amostraId}/custodia`, { sinal: abortar.signal })
      .then((r) => { setLista(r); setErro(null); })
      .catch((e) => { if ((e as Error).name !== 'AbortError') setErro(e); });
    return () => abortar.abort();
  }, [amostraId, chave]);

  return (
    <>
      <h2>Cadeia de custódia</h2>

      {/*
        * O aviso segue o eixo da CUSTÓDIA, não o de aceitação. Antes ele
        * lia `status`, que hoje carrega as duas coisas misturadas — e o
        * dia em que o status legado deixar de existir, este aviso deixaria
        * de aparecer sem ninguém notar.
        */}
      {TERMINAIS.has(estado) && (
        <Aviso
          tipo="info"
          titulo={`A amostra foi ${motivo ? rotulo(motivo) : 'encerrada'}.`}
        >
          Saiu da custódia da Testesolo: o banco não aceita mais movimentação
          nenhuma sobre ela. O histórico abaixo continua, e continua
          inalterável.
        </Aviso>
      )}

      <ErroDaApi erro={erro} />
      {lista === null && !erro && <Carregando texto="Carregando a custódia" />}

      {lista && lista.length === 0 && (
        <Vazio>
          Nenhuma movimentação registrada. Enquanto a amostra não for guardada,
          não existe registro de onde ela está.
        </Vazio>
      )}

      {lista && lista.length > 0 && (
        <div className="cartao">
          <div className="rola">
            <table className="dados">
              <thead>
                <tr>
                  <th>Quando</th><th>O quê</th><th>Onde</th><th>Quem</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((e, i) => (
                  <tr key={`${e.datahora}-${i}`}>
                    <td>{dataHora(e.datahora)}</td>
                    <td>
                      {rotulo(e.tipo_evento)}
                      {e.observacao && <div className="sub">{e.observacao}</div>}
                    </td>
                    <td>{e.localizacao_fisica ?? <span className="nada">—</span>}</td>
                    <td>
                      {e.usuario_nome ?? e.usuario_email ?? <span className="nada">—</span>}
                      {e.dispositivo && (
                        <div className="sub" title={e.dispositivo}>
                          {dispositivoResumido(e.dispositivo)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="rodape-regra">
            Movimentação não se apaga nem se corrige: a tabela é somente-inserção,
            e o banco recusa tanto o <span className="mono">UPDATE</span> quanto o{' '}
            <span className="mono">DELETE</span>. Um lançamento errado se conserta
            com o lançamento seguinte, e os dois ficam — que é o que a cadeia de
            custódia do item 6 do briefing exige.
          </p>
        </div>
      )}
    </>
  );
}

/**
 * Guardar.
 *
 * O que sobrou do formulário de seis botões que existia até a onda 23.
 * Quatro daqueles seis o sistema agora deriva do ensaio; este é um dos
 * dois que dependem de um olho humano, e é UM toque: o último local já
 * vem escolhido.
 *
 * O local vem do cadastro e não de um campo de texto porque, com texto
 * livre, `camara umida`, `Câmara Úmida` e `CU` viram três lugares e o
 * inventário do laboratório nunca fecha.
 */
function FormGuardar({
  amostra, aoFechar, aoPronto,
}: { amostra: Amostra; aoFechar(): void; aoPronto(): void }) {
  const [locais, setLocais] = useState<LocalOpcao[] | null>(null);
  const [local, setLocal] = useState<string | null>(amostra.local_id ?? null);
  const [obs, setObs] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api<LocalOpcao[]>('/locais?situacao=ativo')
      .then(setLocais)
      .catch(setErro);
  }, []);

  const enviar = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!local) { setErro(new Error('Escolha onde a amostra foi guardada.')); return; }
    setEnviando(true);
    setErro(null);
    try {
      await api(`/amostras/${amostra.id}/guardar`, {
        metodo: 'POST',
        corpo: { localId: local, observacao: obs.trim() || undefined },
      });
      aoPronto();
    } catch (x) {
      setErro(x);
    } finally {
      setEnviando(false);
    }
  };

  const opcoes = (locais ?? []).map((l) => ({
    valor: l.id,
    texto: l.armazenadas > 0 ? `${l.nome} · ${l.armazenadas} guardadas` : l.nome,
  }));

  return (
    <Dialogo aberto titulo="Guardar amostra" aoFechar={aoFechar}>
      <form onSubmit={enviar}>
        <p className="sub">
          {nomeDaAmostra(daApi(amostra))}
          {amostra.local_nome && ` · saiu de ${amostra.local_nome}`}
        </p>

        <ErroDaApi erro={erro} />

        {locais !== null && locais.length === 0 && (
          <Aviso tipo="atencao" titulo="Nenhum local cadastrado ainda.">
            O sistema não inventa prateleira. Cadastre os lugares em
            Locais de armazenamento — prateleira, câmara úmida, bancada — e
            volte aqui.
          </Aviso>
        )}

        <Escolha
          rotulo="Onde ela foi guardada"
          opcoes={opcoes}
          valor={local}
          aoEscolher={setLocal}
          limpavel={false}
          dica={amostra.local_nome
            ? `Último lugar registrado: ${amostra.local_nome}. Confirme ou troque.`
            : 'É o que a próxima pessoa vai procurar.'}
        />

        <Area
          rotulo="Observação"
          value={obs}
          onChange={(e) => setObs(e.currentTarget.value)}
          rows={2}
          dica="Opcional. Vai junto na cadeia de custódia."
        />

        <div className="linha" style={{ marginTop: '1rem' }}>
          <button type="button" className="botao secundario" onClick={aoFechar}>
            Cancelar
          </button>
          {/*
            * "Confirmar guarda" e não "Guardar": o botão que ABRE este
            * diálogo já se chama Guardar, e dois controles com o mesmo
            * nome na mesma tela é ambiguidade para quem lê e para quem
            * testa. O verbo do diálogo confirma o que o de fora começou.
            */}
          <button type="submit" className="botao" disabled={enviando || !local}>
            {enviando ? 'Gravando…' : 'Confirmar guarda'}
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

/**
 * Encerrar.
 *
 * O motivo vai colado, e não é burocracia: devolvida, consumida e
 * descartada são três coisas diferentes que uma auditoria pergunta. Com
 * um estágio só chamado "descartada", a cadeia de custódia passaria a
 * afirmar que material que ainda existe foi destruído.
 */
function FormEncerrar({
  amostra, aoFechar, aoPronto,
}: { amostra: Amostra; aoFechar(): void; aoPronto(): void }) {
  const [motivo, setMotivo] = useState<string | null>(null);
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const escolhida = SAIDAS.find((s) => s.valor === motivo);

  /*
   * «Descartada» só cabe depois do prazo de guarda — e o banco recusa
   * antes disso (RN-183, 0153). A opção fica visível e desativada com o
   * motivo, para o técnico não gastar a justificativa num gesto que vai
   * ser recusado. Amostra que nunca teve ensaio na conta descarta livre.
   */
  /*
   * Desde a 0175 o motivo vem PRONTO do banco (`impedimento_texto`): é
   * a mesma frase que o gatilho usa na recusa, e um impedimento por vez
   * — retida, ensaio em aberto, laudo não aprovado, não emitido, não
   * entregue ao cliente, prazo correndo. Recalcular aqui seria a tela
   * contar uma história e o banco recusar por outra.
   */
  const sit = amostra.situacao;
  const semDescarte: string | null = sit && !sit.elegivel_descarte
    ? (sit.impedimento_texto ?? 'O banco recusa o descarte desta amostra agora.')
    : null;
  const saidas = SAIDAS.map((s) => (s.valor === 'descartada' && semDescarte
    ? { ...s, diz: semDescarte, desabilitada: true, porque: semDescarte }
    : s));

  const enviar = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!motivo) { setErro(new Error('Diga por que a amostra está saindo.')); return; }
    setEnviando(true);
    setErro(null);
    try {
      await api(`/amostras/${amostra.id}/encerrar`, {
        metodo: 'POST',
        corpo: { motivo, justificativa: justificativa.trim() },
      });
      aoPronto();
    } catch (x) {
      setErro(x);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialogo aberto titulo="Encerrar amostra" aoFechar={aoFechar}>
      <form onSubmit={enviar}>
        <p className="sub">{nomeDaAmostra(daApi(amostra))}</p>

        <ErroDaApi erro={erro} />

        <Escolha
          rotulo="O que aconteceu com o material"
          opcoes={saidas.map(({ valor, texto, desabilitada, porque }) =>
            ({ valor, texto, desabilitada, porque }))}
          valor={motivo}
          aoEscolher={setMotivo}
          limpavel={false}
          dica={escolhida?.diz
            ?? semDescarte
            ?? 'As três saídas são fatos diferentes. Escolha para ver o que cada uma grava.'}
        />

        <Area
          rotulo="Justificativa"
          value={justificativa}
          onChange={(e) => setJustificativa(e.currentTarget.value)}
          rows={2}
          erro={doCampoMov(erro, 'justificativa')}
          dica="Obrigatória: este lançamento não se desfaz."
          required
        />

        <Aviso tipo="atencao" titulo="Este lançamento é final.">
          Depois dele a amostra não volta, e registro de custódia não se
          apaga. Se ainda houver ensaio pendente ou prazo de guarda
          correndo, o cartão no alto da tela avisa — leia antes de gravar.
        </Aviso>

        <div className="linha" style={{ marginTop: '1rem' }}>
          <button type="button" className="botao secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button
            type="submit"
            className="botao"
            disabled={enviando || !motivo || justificativa.trim().length < 5}
          >
            {enviando ? 'Gravando…' : 'Encerrar'}
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

/** O erro do servidor no campo a que ele se refere, quando ele diz qual é. */
const doCampoMov = (erro: unknown, campo: string) =>
  (erro instanceof ErroApi ? erro.campo(campo) : undefined);

// ── Guarda e descarte ────────────────────────────────────────────────

/**
 * O cartão que responde a pergunta da prateleira.
 *
 * Desde a 0175 a resposta tem QUATRO condições, e o cartão diz qual
 * está faltando — uma por vez, na ordem em que ela aparece no banco:
 *
 *   ensaios aprovados → laudo aprovado → laudo emitido →
 *   laudo DISPONIBILIZADO ao cliente → prazo de guarda vencido.
 *
 * A contagem começa na entrega ao cliente, e não na aprovação: aprovar
 * é ato interno, e o cliente pode ficar semanas sem saber que o laudo
 * existe. É por isso que o laudo emitido e não entregue ganha, aqui
 * mesmo, o botão de entregar.
 *
 * Retenção a pedido do cliente (RN-184) e autorização de descarte
 * antecipado (gestor/RT) são registros próprios, e aparecem com quem,
 * quando e por quê.
 */
function Guarda({ situacao, encerrada, motivo, amostra, aoMudar }: {
  situacao: Situacao | null; encerrada?: boolean; motivo?: string | null;
  amostra: Amostra; aoMudar(): void;
}) {
  const { pode } = useSessao();
  const [acao, setAcao] = useState<'reter' | 'soltar' | 'autorizar' | 'cancelar' | 'desistencia' | null>(null);
  const [entregando, setEntregando] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  if (!situacao) return null;

  /*
   * Amostra encerrada não tem contagem de guarda a mostrar. A auditoria
   * (UX-055) viu «DESCARTADA» e «faltam 15 dias para o descarte» na
   * mesma ficha — o cartão contava um prazo para material que já saiu.
   */
  if (encerrada) {
    return (
      <div className="aviso" role="status">
        <strong>Amostra encerrada
          {motivo ? ` — ${rotulo(motivo)}.` : '.'}</strong>
        {' '}A custódia terminou; o histórico abaixo diz quando e por quem.
      </div>
    );
  }

  const total = Number(situacao.ensaios ?? 0);
  const naConta = Number(situacao.ensaios_na_conta ?? 0);
  const pendentes = Number(situacao.ensaios_pendentes ?? 0);
  const dias = situacao.dias_para_descarte;
  const imp = situacao.impedimento_descarte;

  /* Laudos emitidos e ainda não entregues — cada um com o botão. */
  const laudosParaEntregar = amostra.ensaios
    .filter((e) => e.relatorio_id && e.relatorio_status === 'emitido')
    .reduce<Ensaio[]>((lista, e) => (
      lista.some((x) => x.relatorio_id === e.relatorio_id) ? lista : [...lista, e]), []);

  async function entregar(relatorioId: string) {
    setEntregando(relatorioId); setErro(null);
    try {
      await api(`/descarte/laudo/${relatorioId}/disponibilizar`, { metodo: 'POST' });
      aoMudar();
    } catch (e) { setErro(e); } finally { setEntregando(null); }
  }

  let corpo: React.ReactNode;
  let tom: 'ok' | 'atencao' | 'erro' | '' = 'atencao';

  if (total === 0) {
    corpo = (
      <>
        <strong>Nenhum ensaio programado para esta amostra.</strong>
        {' '}Ela não está concluída — está parada. Guardar por engano uma amostra
        que ninguém programou é diferente de guardar uma que ainda vai ser ensaiada.
      </>
    );
  } else if (naConta === 0) {
    tom = '';
    corpo = (
      <>
        <strong>Todos os ensaios desta amostra foram cancelados.</strong>
        {' '}Não há prazo de guarda a cumprir: pode ser descartada com justificativa.
      </>
    );
  } else if (imp === 'ensaio_em_aberto' || (!situacao.concluida && pendentes > 0)) {
    corpo = (
      <>
        <strong>
          {plural(pendentes, 'ensaio ainda não foi concluído', 'ensaios ainda não foram concluídos')}.
        </strong>
        {' '}<b>Não descartar.</b> A contagem de guarda só começa quando o laudo
        chega ao cliente.
      </>
    );
  } else if (imp === 'laudo_nao_aprovado') {
    corpo = (
      <>
        <strong>Os ensaios terminaram, mas o laudo ainda não foi aprovado.</strong>
        {' '}<b>Não descartar.</b> Faltam a aprovação, a emissão e a entrega ao cliente.
      </>
    );
  } else if (imp === 'laudo_nao_emitido') {
    corpo = (
      <>
        <strong>Laudo aprovado e ainda não emitido.</strong>
        {' '}Falta o PDF oficial. A contagem de guarda só começa quando o laudo
        emitido chega ao cliente.
      </>
    );
  } else if (imp === 'laudo_nao_entregue' || (!situacao.entregue && situacao.concluida && !situacao.descarte_autorizado)) {
    corpo = (
      <>
        <strong>Laudo emitido e ainda não disponibilizado ao cliente.</strong>
        {' '}A contagem de {plural(situacao.prazo_guarda_dias ?? 0, 'dia', 'dias')} de guarda
        só começa na entrega — <b>não descartar</b> antes dela.
        {laudosParaEntregar.length > 0 && (
          <div className="acoes" style={{ marginTop: '.5rem' }}>
            {laudosParaEntregar.map((e) => (pode('relatorio.disponibilizar') ? (
              <button key={e.relatorio_id!} type="button" className="botao"
                disabled={entregando !== null}
                onClick={() => void entregar(e.relatorio_id!)}>
                {entregando === e.relatorio_id
                  ? 'Entregando…'
                  : `Disponibilizar o laudo ${e.relatorio} · R${
                    String(e.relatorio_revisao ?? 0).padStart(2, '0')} ao cliente`}
              </button>
            ) : (
              <span key={e.relatorio_id!} className="sub">
                Laudo {e.relatorio} emitido — entregar exige a permissão{' '}
                <b>relatorio.disponibilizar</b>.
              </span>
            )))}
          </div>
        )}
      </>
    );
  } else if (situacao.descarte_autorizado && !(situacao.entregue && dias !== null && dias <= 0)) {
    tom = 'ok';
    corpo = (
      <>
        <strong>Descarte antecipado autorizado — pode ser descartada.</strong>
        {' '}Autorização válida até {data(situacao.autorizacao_valida_ate)}:{' '}
        {situacao.autorizacao_motivo}. A baixa consome a autorização.
      </>
    );
  } else {
    const vencido = dias !== null && dias <= 0;
    tom = vencido ? 'erro' : 'ok';
    corpo = (
      <>
        <strong>
          {vencido
            ? 'Prazo de guarda vencido — pode ser descartada.'
            : `Faltam ${plural(dias ?? 0, 'dia', 'dias')} para o descarte.`}
        </strong>
        {' '}Laudo entregue ao cliente em {data(situacao.relatorio_disponibilizado_em)}.
        Guarda de {plural(situacao.prazo_guarda_dias ?? 0, 'dia', 'dias')} pela OS
        {situacao.descarte_a_partir_de
          && <>, liberando em <b>{data(situacao.descarte_a_partir_de)}</b></>}.
      </>
    );
  }

  const podeReter = pode('amostra.reter');
  /*
   * 229 (`RN-210`): o cliente desistiu — só quando há ensaio ainda por
   * fazer. Sem ensaio na conta a baixa comum já passa; com ensaio aprovado
   * e nada pendente, o que vale é o prazo de guarda do laudo.
   */
  const cabeDesistencia = pode('amostra.descartar') && !situacao.retida && pendentes > 0;
  const podeAutorizar = pode('amostra.autorizar_descarte');
  /* Autorizar só faz sentido quando é a ESPERA que trava — não o ensaio. */
  const cabeAutorizar = podeAutorizar && naConta > 0 && situacao.concluida
    && !situacao.retida && !situacao.descarte_autorizado && !situacao.elegivel_descarte;

  return (
    <div className="guarda-e-descarte">
      {situacao.retida && (
        <div className="aviso atencao retida" role="status">
          <strong>Retida a pedido do cliente
            {situacao.retencao_ate ? ` até ${data(situacao.retencao_ate)}` : ''}.</strong>
          {' '}{situacao.retencao_motivo}
          {situacao.retencao_pedido_por && <> — pedido por {situacao.retencao_pedido_por}</>}.
          {' '}Enquanto vigorar, o descarte é recusado pelo banco (RN-184) — nem
          autorização passa por cima.
        </div>
      )}

      {!situacao.retida && (
        <div className={`aviso ${tom}`.trim()} role="status">{corpo}</div>
      )}

      <ErroDaApi erro={erro} />

      <div className="acoes-guarda">
        {podeReter && !situacao.retida && (
          <button type="button" className="botao secundario" onClick={() => setAcao('reter')}>
            Reter a pedido do cliente
          </button>
        )}
        {podeReter && situacao.retida && (
          <button type="button" className="botao secundario" onClick={() => setAcao('soltar')}>
            Soltar a retenção
          </button>
        )}
        {cabeAutorizar && (
          <button type="button" className="botao secundario" onClick={() => setAcao('autorizar')}>
            Autorizar descarte antecipado
          </button>
        )}
        {cabeDesistencia && (
          <button type="button" className="botao secundario" onClick={() => setAcao('desistencia')}>
            O cliente desistiu — descartar sem ensaiar
          </button>
        )}
        {podeAutorizar && situacao.descarte_autorizado && (
          <button type="button" className="botao discreto" onClick={() => setAcao('cancelar')}>
            Cancelar a autorização
          </button>
        )}
      </div>

      {acao && (
        <FormDescarteAto
          amostra={amostra}
          acao={acao}
          aoFechar={() => setAcao(null)}
          aoPronto={() => { setAcao(null); aoMudar(); }}
        />
      )}
    </div>
  );
}

/**
 * Os quatro atos que rodeiam o descarte, num formulário só: reter,
 * soltar, autorizar, cancelar a autorização. Todos exigem o motivo
 * escrito — o banco recusa em branco.
 */
function FormDescarteAto({ amostra, acao, aoFechar, aoPronto }: {
  amostra: Amostra;
  acao: 'reter' | 'soltar' | 'autorizar' | 'cancelar' | 'desistencia';
  aoFechar(): void; aoPronto(): void;
}) {
  const [motivo, setMotivo] = useState('');
  const [pedidoPor, setPedidoPor] = useState('');
  const [ate, setAte] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const TITULO = {
    reter: 'Reter a pedido do cliente',
    soltar: 'Soltar a retenção',
    autorizar: 'Autorizar descarte antecipado',
    cancelar: 'Cancelar a autorização de descarte',
    desistencia: 'O cliente desistiu — descartar sem ensaiar',
  }[acao];
  const [destinacao, setDestinacao] = useState('');

  const enviar = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setEnviando(true); setErro(null);
    try {
      const corpo: Record<string, unknown> = { motivo: motivo.trim() };
      if (acao === 'reter') {
        if (pedidoPor.trim()) corpo.pedidoPor = pedidoPor.trim();
        if (ate) corpo.ate = ate;
      }
      if (acao === 'autorizar' && ate) corpo.validaAte = ate;
      /*
       * Quatro chamadas escritas por extenso, e não uma com o verbo
       * interpolado: o teste 26 lê o FONTE para saber que rota de
       * criação tem caminho na tela, e `${ROTA}` seria invisível para ele.
       */
      if (acao === 'reter') {
        await api(`/descarte/amostra/${amostra.id}/reter`, { metodo: 'POST', corpo });
      } else if (acao === 'soltar') {
        await api(`/descarte/amostra/${amostra.id}/soltar`, { metodo: 'POST', corpo });
      } else if (acao === 'autorizar') {
        await api(`/descarte/amostra/${amostra.id}/autorizar`, { metodo: 'POST', corpo });
      } else if (acao === 'desistencia') {
        if (destinacao.trim()) corpo.destinacao = destinacao.trim();
        await api(`/descarte/amostra/${amostra.id}/desistencia`, { metodo: 'POST', corpo });
      } else {
        await api(`/descarte/amostra/${amostra.id}/cancelar-autorizacao`,
          { metodo: 'POST', corpo });
      }
      aoPronto();
    } catch (x) {
      setErro(x);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialogo aberto titulo={TITULO} aoFechar={aoFechar}>
      <form onSubmit={enviar}>
        <p className="sub">{nomeDaAmostra(daApi(amostra))}</p>
        <ErroDaApi erro={erro} />

        <Area
          rotulo="Motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.currentTarget.value)}
          rows={2}
          erro={doCampoMov(erro, 'motivo')}
          dica={acao === 'reter'
            ? 'O que o cliente pediu, com as palavras dele.'
            : acao === 'autorizar'
              ? 'Por que o material pode sair antes do prazo. Fica na trilha, com o seu nome.'
              : acao === 'desistencia'
                ? 'O que o cliente pediu, e quem pediu. Fica na trilha, com o seu nome.'
              : 'Obrigatório: fica no registro, com quem e quando.'}
          required
        />

        {acao === 'reter' && (
          <>
            <Campo rotulo="Quem pediu, no cliente" value={pedidoPor}
              onChange={(e) => setPedidoPor(e.currentTarget.value)}
              placeholder="Nome e função, se souber" />
            <Campo rotulo="Reter até" type="date" value={ate}
              onChange={(e) => setAte(e.currentTarget.value)}
              dica="Em branco, a retenção vigora até alguém soltá-la." />
          </>
        )}
        {acao === 'autorizar' && (
          <Campo rotulo="Válida até" type="date" value={ate}
            onChange={(e) => setAte(e.currentTarget.value)}
            dica="Em branco, 30 dias. A baixa consome a autorização; vencida, ela some sozinha." />
        )}

        {acao === 'desistencia' && (
          <>
            <Campo rotulo="Destinação do material" value={destinacao}
              onChange={(e) => setDestinacao(e.currentTarget.value)}
              placeholder="Ex.: descarte em caçamba; devolvido ao cliente" />
            <Aviso tipo="atencao" titulo="Os ensaios que ainda não começaram são cancelados.">
              Cancelados, não contam no escopo, nos quantitativos nem na medição,
              e a amostra sai do Mapa Geotécnico. Ensaio que já começou na bancada
              trava a desistência — termine ou cancele por lá.
            </Aviso>
          </>
        )}
        {acao === 'reter' && (
          <Aviso tipo="info" titulo="Trava de banco, não de tela.">
            Enquanto a retenção vigorar, nenhum caminho — nem a API — descarta
            esta amostra (RN-184). Soltar é outro ato, com registro próprio.
          </Aviso>
        )}
        {acao === 'autorizar' && (
          <Aviso tipo="atencao" titulo="A autorização dispensa a espera, não o ensaio.">
            Ela permite descartar antes da entrega ao cliente ou do fim do prazo
            de guarda. Ensaio em aberto e retenção do cliente continuam travando.
          </Aviso>
        )}

        <div className="linha" style={{ marginTop: '1rem' }}>
          <button type="button" className="botao secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="submit" className="botao"
            disabled={enviando || motivo.trim().length < (acao === 'desistencia' ? 10 : 5)}>
            {enviando ? 'Gravando…' : TITULO}
          </button>
        </div>
      </form>
    </Dialogo>
  );
}

// ── Ensaios ──────────────────────────────────────────────────────────

/**
 * A escada do §12, desenhada.
 *
 * Seis degraus, e o ensaio está em um deles. É a mesma escada que o
 * banco percorre (`fn_ensaio_progresso`, 0064) — desenhá-la aqui com
 * outros degraus faria a tela contar uma história e o número contar
 * outra.
 *
 * A cor NUNCA é o único sinal: cada degrau tem forma (cheio, vazio) e
 * rótulo, e o degrau atual leva `aria-current`. §18 do briefing —
 * «identificação visual por cores sem depender exclusivamente delas».
 */
const DEGRAUS = [
  { chave: 'programado',             curto: 'Programado' },
  { chave: 'em_execucao',            curto: 'Na bancada' },
  { chave: 'dados_completos',        curto: 'Dados' },
  { chave: 'em_processamento',       curto: 'Processando' },
  { chave: 'aguardando_verificacao', curto: 'Verificação' },
  { chave: 'aprovado',               curto: 'Aprovado' },
];

/** Em que degrau cada status coloca o ensaio. Espelha a função do banco. */
const DEGRAU_DO_STATUS: Record<string, number> = {
  programado: 0,
  em_execucao: 1, pausado: 1,
  dados_completos: 2,
  /* Devolvido volta ao degrau de quem tem os dados e precisa refazer —
     igualzinho ao banco. Barra que sobe com retrabalho mente. */
  devolvido_para_correcao: 2,
  em_processamento: 3,
  aguardando_verificacao: 4,
  aprovado: 5,
};

function Escada({ status }: { status: string }) {
  const atual = DEGRAU_DO_STATUS[status];

  // Fora da escada: cancelado e substituído não estão "em nenhum ponto",
  // estão fora da conta. Fingir um degrau seria pior que não desenhar.
  if (atual === undefined) {
    return <span className="fora-da-escada">{rotulo(status)} — fora da conta</span>;
  }

  const devolvido = status === 'devolvido_para_correcao';
  const pausado = status === 'pausado';

  return (
    <div className="escada" role="img"
      aria-label={`${rotulo(status)} — degrau ${atual + 1} de ${DEGRAUS.length}`}>
      {DEGRAUS.map((d, i) => (
        <span
          key={d.chave}
          className={[
            'degrau',
            i < atual ? 'andado' : '',
            i === atual ? 'agora' : '',
            i === atual && devolvido ? 'devolvido' : '',
            i === atual && pausado ? 'pausado' : '',
          ].filter(Boolean).join(' ')}
          aria-current={i === atual ? 'step' : undefined}
          title={d.curto}
        />
      ))}
      <span className="degrau-nome">
        {devolvido ? 'Devolvido para correção'
          : pausado ? 'Pausado na bancada'
          : DEGRAUS[atual].curto}
      </span>
    </div>
  );
}

/**
 * O avanço, em barra e em número.
 *
 * Os DOIS, sempre. «75 %» com zero aprovados e «75 %» com três de quatro
 * aprovados são situações operacionalmente diferentes, e só a contagem
 * separa as duas — quem lê só a barra conclui que está quase pronto
 * quando não há nada assinado.
 */
function Avanco({
  pct, aprovados, total, devolvidos,
}: {
  pct: number | null; aprovados: number; total: number; devolvidos: number;
}) {
  if (pct === null || total === 0) return null;

  return (
    <div className="avanco">
      <BarraDeAvanco pct={pct} rotulo="Avanço dos ensaios" />
      <p className="avanco-diz">
        {/* 373: «N de M aprovados», com o plural da casa (P-TXT-11). */}
        <strong>{aprovados}</strong> de <strong>{total}</strong>{' '}
        {total === 1 ? 'aprovado' : 'aprovados'}
        {devolvidos > 0 && (
          <> · <span className="alerta-inline">
            {plural(devolvidos, 'devolvido', 'devolvidos')} para correção
          </span></>
        )}
      </p>
    </div>
  );
}

/**
 * O ciclo da amostra dentro da empresa — e o próximo passo de cada ensaio.
 *
 * «se alguém escanear o qrcode de uma amostra, saberá todo o ciclo dela
 *  dentro da empresa e do processo» — e é isso que esta seção responde,
 * de pé na prateleira, sem abrir outra tela.
 *
 * ── O QR leva à bancada (onda 373) ───────────────────────────────────
 *
 * «Ao acessar o QR code de uma amostra, aparece a lista de ensaios
 *  cadastrados nela. Preciso que tenha um botão de fácil acesso,
 *  principalmente no celular, para que os operadores executem o ensaio.
 *  Assim incentiva o uso do QR code e facilita a execução.» — Humberto,
 *  03/11/2026 (opção A).
 *
 * Os ensaios abertos vêm primeiro, cada um num cartão com o botão de 48 px
 * da base: «Lançar», «Corrigir» ou «Verificar» — a MESMA regra da Fila
 * (`acaoDoEnsaio.ts`), para a prateleira e a fila nunca discordarem. O LA,
 * de duas fichas, tem «Lançar LL» e «Lançar LP». Sem ação (a amostra que
 * não chegou, o método sem ficha), o cartão diz o motivo no lugar do botão.
 * Os aprovados, cancelados e substituídos ficam recolhidos atrás de «Ver os
 * N ensaios aprovados»: quem lê o QR para executar não precisa rolar o que
 * já acabou.
 *
 * O botão só aparece para quem pode fazer aquilo (`ensaio.lancar`; o
 * «Verificar», `ensaio.verificar`, como a API exige). Quem não pode vê a
 * lista como antes — e o servidor continua barrando no gravar.
 */
function Programacao({ amostra }: { amostra: Amostra }) {
  const { pode } = useSessao();
  const [verFechados, setVerFechados] = useState(false);
  const s = amostra.situacao;
  const total = Number(s?.ensaios_na_conta ?? amostra.ensaios.length);
  const aprovados = Number(s?.ensaios_aprovados ?? 0);
  const devolvidos = Number(s?.ensaios_devolvidos ?? 0);
  const pct = s?.avanco_pct ?? null;
  const naBancada = { lancar: pode('ensaio.lancar'), verificar: pode('ensaio.verificar') };
  const mostraMotivo = naBancada.lancar || naBancada.verificar;

  if (!amostra.ensaios.length) {
    return (
      <section className="ensaios-da-amostra" aria-labelledby="ensaios-da-amostra">
        <h2 id="ensaios-da-amostra">Ensaios para executar</h2>
        <Vazio>
          Nenhum ensaio programado. Com a OS atribuída, a programação prévia
          da OS gera os ensaios desta amostra.
        </Vazio>
      </section>
    );
  }

  const abertos = amostra.ensaios.filter((e) => e.aberto);
  const fechados = amostra.ensaios.filter((e) => !e.aberto);
  const soAprovados = fechados.every((e) => e.status === 'aprovado');
  const nomeDosFechados = fechados.length === 1
    ? (soAprovados ? 'o ensaio aprovado' : 'o ensaio fora da bancada')
    : `os ${fechados.length} ensaios ${soAprovados ? 'aprovados' : 'aprovados ou cancelados'}`;

  /* O pé de cada cartão: o relatório, como sempre foi. */
  const pe = (e: Ensaio) => (
    <div className="ensaio-pe">
      {e.relatorio
        ? (
          <span>
            {e.relatorio} R{String(e.relatorio_revisao ?? 0).padStart(2, '0')}
            {e.relatorio_status && ` · ${rotulo(e.relatorio_status)}`}
          </span>
        )
        : <span className="nada">sem relatório</span>}
      {e.data_aprovacao && <span> · aprovado em {data(e.data_aprovacao)}</span>}
    </div>
  );

  return (
    <section className="ensaios-da-amostra" aria-labelledby="ensaios-da-amostra">
      <h2 id="ensaios-da-amostra">Ensaios para executar</h2>
      <div className="cartao">
        <Avanco pct={pct} aprovados={aprovados} total={total}
          devolvidos={devolvidos} />

        {abertos.length > 0 ? (
          <ul className="lista-ensaios">
            {abertos.map((e) => {
              const botoes = botoesDoEnsaio(e, naBancada);
              const motivo = mostraMotivo ? motivoSemAcao(e) : null;
              return (
                <li key={`${e.id}-${e.relatorio_id ?? ''}`} className="ensaio-aberto" data-ensaio={e.id}>
                  <div className="ensaio-cabeca">
                    <strong>{e.metodo}</strong>
                    <span className="sub">{e.metodo_codigo}</span>
                  </div>

                  <Escada status={e.status} />

                  {botoes.length > 0 ? (
                    /* Uma ação primária por cartão (P-UI-10): no LA a
                       primeira ficha é o primário, a segunda o secundário. */
                    <div className="ensaio-acoes">
                      {botoes.map((b, i) => (
                        <Link key={b.para} className={i === 0 ? 'botao' : 'botao secundario'}
                          to={b.para}>{b.texto}</Link>
                      ))}
                    </div>
                  ) : motivo ? (
                    <p className={`ensaio-motivo${e.impedimento_amostra
                      ? (e.impedimento_definitivo ? ' critico-linha' : ' aviso-linha') : ' nada'}`}>
                      {motivo}
                    </p>
                  ) : null}

                  {pe(e)}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="ensaios-nada">Nenhum ensaio para executar nesta amostra.</p>
        )}

        {fechados.length > 0 && (
          <>
            <button type="button" className="botao discreto ver-fechados"
              aria-expanded={verFechados} aria-controls="ensaios-fechados"
              onClick={() => setVerFechados(!verFechados)}>
              {verFechados ? `Esconder ${nomeDosFechados}` : `Ver ${nomeDosFechados}`}
            </button>
            <ul className="lista-ensaios" id="ensaios-fechados" hidden={!verFechados}>
              {fechados.map((e) => (
                <li key={`${e.id}-${e.relatorio_id ?? ''}`} data-ensaio={e.id}>
                  <div className="ensaio-cabeca">
                    <strong>{e.metodo}</strong>
                    <span className="sub">{e.metodo_codigo}</span>
                  </div>
                  <Escada status={e.status} />
                  {pe(e)}
                </li>
              ))}
            </ul>
          </>
        )}

        <p className="rodape-regra">
          O avanço vem da escada do §12: cada degrau que o ensaio sobe vale
          um pedaço. Ensaio devolvido para correção VOLTA de degrau — uma
          barra que cresce com retrabalho estaria mentindo. Cancelado e
          substituído saem da conta.
        </p>
      </div>
    </section>
  );
}
